import storeManager from '../../store/StoreManager';
import serviceWorkerCacheHandler from './serviceWorkerCacheHandler';
import domCacheHandler from './domCacheHandler';
import mediaFinder from './mediaFinder';
import generalResourcesCacher from './generalResourcesCacher';
import ServiceWorkerService from '../../services/ServiceWorkerService';
import settingsReader from '../../store/readHelpers/SettingsReader';
import cacheStatusActions, { cacheStatusTypes, cacheResultTypes } from '../../actions/CacheStatusActions';
import QuestionDataIdentifier from '../../services/sessionParameters/QuestionDataIdentifier';
import * as sessionConstants from '../../constants/SessionConstants';
import AsyncRetryer from '../../services/AsyncRetryer';
import utilService from '../../services/UtilService';
import mediaResourcesActions from '../../actions/MediaResourcesActions';
import constants from '../../qti/Constants';
import loadProgressHandler from './loadProgressHandler';
import backgroundAudioService from '../../services/BackgroundAudioService';
import marketplacePropertiesService from '../../services/MarketplacePropertiesService';
import assessmentReader from '../../store/readHelpers/AssessmentReader';
import sessionReader from '../../store/readHelpers/SessionReader';
import PDFJSLoader from './libraries/PDFJS';
import { generateFileUrlForLogicalName } from '../../qti/parser/question/MediaModels';
import { reportError } from '../../services/ErrorReporting';

export const cacheContexts = {
    INFO_DOCS: 'info_docs',
    GENERAL_RESOURCES: 'general_resources',
    READINESS_CHECK: 'readiness_check',
    TEST: 'test',
};

export function dispatchCachedSource(identifier, url) {
    storeManager.dispatch(
        mediaResourcesActions.setCachedSource(identifier, url)
    );
}

const cacheManager = {
    cacheHandler: null,
    abortController: null,
    getAbortController() {
        try {
            if (!this.abortController) {
                this.abortController = new AbortController();
            }
            return this.abortController;
        } catch (error) {
            console.error('AbortController not supported', error);
            return null;
        }
    },
    hasAborted() {
        return !!this.getAbortController()?.signal?.aborted;
    },

    getAllAssessmentRunIds() {
        const { assessment, pendingAssessments } = storeManager.getState();
        const list = Object.keys(pendingAssessments);
        if (assessment.assessmentRunId) {
            list.push(assessment.assessmentRunId);
        }

        return list;
    },

    isCacheable(file) {
        return !utilService.isDataURL(file.url);
    },

    cacheIsDone(identifier, { cacheStatus } = storeManager.getState()) {
        return cacheStatus[identifier]?.status === cacheStatusTypes.DONE;
    },

    allCacheItemsAreDone() {
        const { cacheStatus } = storeManager.getState();
        return Object.values(cacheStatus).every(item => item.status === cacheStatusTypes.DONE);
    },

    reportCacheStatus(identifier, status, result) {
        storeManager.dispatch(
            cacheStatusActions.setStatus(identifier, status, result)
        );
    },

    /**
     * Caches both readiness check and test resources. All media to be used will be loaded and cached
     */
    async cacheQuestionSetMedia(questionSet, cacheContext) {
        const { assessmentRunId, contentRevisionId } = questionSet;
        const { AUDIO, FILE } = constants.additionalResourceType;
        const questionDataIdentifier = new QuestionDataIdentifier(sessionConstants.MODE_QUESTION_SET, { assessmentRunId, contentRevisionId });
        const startTime = Date.now();
        console.log('cacheQuestionSetMedia start', cacheContext, questionDataIdentifier.paramValue);
        const offlineResilienceEnabled = marketplacePropertiesService.isOfflineResilienceForPdfViewerEnabled();

        const allMediaModels = mediaFinder
            .getMediaModelsInQuestionSet(questionSet, [FILE, AUDIO])
            .filter(this.isCacheable);

        // When offline resilience is on, FILE additional-resource PDFs are pre-cached in
        // GENERAL_RESOURCES under their stable servlet URL via cachePDFPanelFilesInCacheStorage.
        // Exclude them from the SW-cache pass (cacheMediaModelWithRetries) to avoid a second
        // fetch that would store a duplicate under the presigned (expiring) URL.
        const mediaModels = offlineResilienceEnabled
            ? allMediaModels.filter(m => m.type !== FILE)
            : allMediaModels;

        const pdfPanelModels = offlineResilienceEnabled
            ? mediaFinder.getPDFPanelModelsInQuestionSet(questionSet).filter(this.isCacheable)
            : [];

        this.applyCustomCdnUrls(allMediaModels, cacheContext);

        this.reportCacheStatus(questionDataIdentifier.paramValue, cacheStatusTypes.IN_PROGRESS, '');

        // Cache PDF viewer files and PDF documents before the media-model loop.
        // Wrapped in a best-effort try/catch so that CacheStorage quota errors or
        // caches.open rejections do not abort the main caching flow or skip the
        // existing error-reporting logic below.
        // When pre-caching succeeds and the SW cache handler is active, PDFs are already
        // in GENERAL_RESOURCES — the SW serves them via caches.match() across all caches,
        // so we skip them in allModels to avoid a redundant second network fetch.
        // If pre-caching fails, pdfPreCachedToStorage stays false and BOTH pdfPanelModels
        // AND fileAdditionalResourceModels fall through to cacheMediaModelWithRetries as a
        // fallback, so FILE resource PDFs are not silently left uncached.
        // FILE additional-resource PDFs are stored under their stable /file/cil/ servlet URL
        // (not the presigned S3 URL) so caches.match(cachedServletUrl) in AdditionalResource
        // finds them reliably offline. Presigned URLs expire and differ between sessions.
        const fileAdditionalResourceModels = offlineResilienceEnabled
            ? (questionSet.additionalResources || [])
                .filter(r => r.type === FILE && r.logicalName)
                .map(r => ({ url: generateFileUrlForLogicalName(r.logicalName, '', assessmentRunId, FILE) }))
            : [];

        let pdfPreCachedToStorage = false;
        if (pdfPanelModels.length > 0 || fileAdditionalResourceModels.length > 0) {
            try {
                await PDFJSLoader.cacheViewerFiles();
                await this.cachePDFPanelFilesInCacheStorage([...pdfPanelModels, ...fileAdditionalResourceModels]);
                pdfPreCachedToStorage = this.usesServiceWorkerCache();
            } catch (pdfCacheError) {
                reportError(pdfCacheError);
            }
        }

        try {
            const allModels = mediaModels.concat(pdfPreCachedToStorage ? [] : [...pdfPanelModels, ...fileAdditionalResourceModels]);
            const cachedItems = await Promise.all(
                allModels.map(mediaModel => this.cacheMediaModelWithRetries(mediaModel, cacheContext, questionDataIdentifier))
            );

            const durationSeconds = Math.round((Date.now() - startTime) / 1000);
            console.log(`cacheQuestionSetMedia done in ${durationSeconds} seconds`, cacheContext, questionDataIdentifier.paramValue);

            if (marketplacePropertiesService.isBackgroundAudioCheckEnabled() && assessmentReader.hasBackgroundAudio(assessmentRunId)) {
                await backgroundAudioService.nudgePlayerInstanceCreation(assessmentRunId);
            }

            await this.cacheSymbolicMathFiles(questionSet, questionDataIdentifier);

            const maxTries = allModels.length === 0 ? 1 : Math.max(...cachedItems.map(item => item.startedTries));
            const downloadTime = allModels.length === 0 ? 0 : Date.now() - startTime;

            loadProgressHandler.reportDownloadInfo(assessmentRunId, cacheContext, true, maxTries, downloadTime);
            this.reportCacheStatus(questionDataIdentifier.paramValue, cacheStatusTypes.DONE, cacheResultTypes.COMPLETE);

            return Promise.resolve(`Cached question set AR ${assessmentRunId} CR ${contentRevisionId}`);
        } catch (error) {
            const {
                message,
                startedTries = 0,
            } = error;
            const resultType = this.hasAborted() ? cacheResultTypes.TIMEOUT : cacheResultTypes.INCOMPLETE;
            loadProgressHandler.reportDownloadInfo(assessmentRunId, cacheContext, false, startedTries);
            this.reportCacheStatus(questionDataIdentifier.paramValue, cacheStatusTypes.DONE, resultType);

            console.log(message);
            return Promise.reject(`Failed caching question set AR ${assessmentRunId} CR ${contentRevisionId}`);
        }
    },

    async cacheSymbolicMathFiles(questionSet, questionDataIdentifier) {
        if (!marketplacePropertiesService.isSymbolicMathKeyboardInOfflineModeEnabled()) {
            return;
        }
        const symbolicMathFiles = generalResourcesCacher.getSymbolicMathFilesToCache(questionSet);
        if (symbolicMathFiles.length > 0) {
            await generalResourcesCacher.cacheFiles(
                symbolicMathFiles,
                'Symbolic Math resources',
                filePackage =>
                    this.cacheFilePackage(
                        filePackage,
                        cacheContexts.GENERAL_RESOURCES,
                        questionDataIdentifier.paramValue
                    )
            );
        }
    },

    applyCustomCdnUrls(mediaModels, cacheContext) {
        if (marketplacePropertiesService.isEnableCustomCdnForTestContent()
            && settingsReader.isCustomDynamicCdnConfiguredForTest() && cacheContext === cacheContexts.READINESS_CHECK) {
            const assessmentRunIdReadiness = sessionReader.getAssessmentRunId();
            mediaModels.forEach((model) => {
                if (model?.sources) {
                    model.sources.forEach((source) => {
                        source.url = this.getUpdatedUrl(source, assessmentRunIdReadiness);
                    });
                }
            });
        }
    },

    getUpdatedUrl(source, assessmentRunIdReadiness) {
        if (source?.url && !source.url?.includes('assessmentRunId')) {
            const separator = source.url.includes('?') ? '&' : '?';
            source.url += `${separator}assessmentRunId=${assessmentRunIdReadiness}`;
        }
        return source.url;
    },

    async cacheChildTestInfoDocs(childTests) {
        const startTime = Date.now();
        const { parentTest } = storeManager.getState();
        const questionDataIdentifier = parentTest.sourceTemplateId
            ? new QuestionDataIdentifier('info_docs', { sourceTemplateId: parentTest.sourceTemplateId })
            : new QuestionDataIdentifier('info_docs', { assessmentRunId: parentTest.assessment.assessmentRunId });

        console.log('cacheChildTestInfoDocs start', questionDataIdentifier.paramValue);
        const files = childTests
            .map(childTest => mediaFinder.getMediaModelsInQuestion(childTest.documentModel, childTest.assessmentRunId))
            .reduce((accumulator, models) => {
                return accumulator.concat(models);
            }, [])
            .filter(this.isCacheable);

        try {
            await Promise.all(
                files.map(file => this.cacheMediaModelWithRetries(file, cacheContexts.INFO_DOCS, questionDataIdentifier))
            );

            const durationSeconds = Math.round((Date.now() - startTime) / 1000);
            console.log(`cacheChildTestInfoDocs done in ${durationSeconds} seconds`, questionDataIdentifier.paramValue);

            this.reportCacheStatus(questionDataIdentifier.paramValue, cacheStatusTypes.DONE, cacheResultTypes.COMPLETE);

            return Promise.resolve(`Cached info docs ID ${questionDataIdentifier.paramValue}`);
        } catch (error) {
            this.reportCacheStatus(questionDataIdentifier.paramValue, cacheStatusTypes.DONE, cacheResultTypes.INCOMPLETE);
            return Promise.reject(`Failed caching info docs ID ${questionDataIdentifier.paramValue}`);
        }
    },

    async cacheGeneralResources(assessment) {
        const filePackageCacher = filePackage => this.cacheFilePackage(filePackage, cacheContexts.GENERAL_RESOURCES, assessment.contentRevisionId);
        return generalResourcesCacher.cacheAll(assessment, filePackageCacher);
    },

    async cacheFilePackage(filePackage, cacheContext, questionDataIdentifier) {
        return this
            .getCacheHandler()
            .cacheFilePackage(filePackage, cacheContext, questionDataIdentifier);
    },

    async cacheSingleFileModel(fileModel, cacheContext, questionDataIdentifier) {
        return this
            .getCacheHandler()
            .cacheSingleFile(fileModel, cacheContext, questionDataIdentifier);
    },

    abortResourceLoading() {
        const abortController = this.getAbortController();
        if (abortController) {
            abortController.abort();
        }
        this.getCacheHandler().abortResourceLoading();
    },

    async cacheMediaModel(mediaModel, cacheContext, questionDataIdentifier) {
        const { alternativeUrl, logicalName, url, sources = [] } = mediaModel;
        const urls = [url, alternativeUrl]
            .concat(sources.map(source => source.url))
            .filter(urlString => !!urlString);

        let result;
        for (let i = 0; i < urls.length; i++) {
            // eslint-disable-next-line no-await-in-loop
            result = await this.cacheSingleFileModel({ ...mediaModel, url: urls[i] }, cacheContext, questionDataIdentifier);
            if (result.success === true) {
                dispatchCachedSource(logicalName, url);
                return { ...mediaModel, ...result, cachedUrl: urls[i] };
            }
        }
        if (this.hasAborted()) {
            return Promise.reject(new DOMException('AbortError'));
        }

        return Promise.reject(`Unable to cache media model with logicalName: ${logicalName}`);
    },

    async cacheMediaModelWithRetries(mediaModel, cacheContext, questionDataIdentifier) {
        const functionToTry = () => this.cacheMediaModel(mediaModel, cacheContext, questionDataIdentifier);
        const retryer = new AsyncRetryer(functionToTry, 5, 10000, true, true);
        return retryer.start();
    },

    getCacheHandler() {
        if (this.cacheHandler !== null) {
            return this.cacheHandler;
        }

        if (document.location.search.includes('cacheHandler=sw') && ServiceWorkerService.isControlledByServiceWorker()) {
            this.cacheHandler = serviceWorkerCacheHandler.init();
        } else if (document.location.search.includes('cacheHandler=dom')) {
            this.cacheHandler = domCacheHandler;
        } else if (ServiceWorkerService.isControlledByServiceWorker() && settingsReader.isServiceWorkerCacheEnabled()) {
            this.cacheHandler = serviceWorkerCacheHandler.init();
        } else {
            this.cacheHandler = domCacheHandler;
        }

        console.log('CacheHandler used: ', this.cacheHandler.name);
        return this.cacheHandler;
    },

    usesServiceWorkerCache() {
        return this.getCacheHandler().name === serviceWorkerCacheHandler.name;
    },

    /**
     * Caches PDF panel files directly into GENERAL_RESOURCES Cache Storage so the service
     * worker can serve them offline. Stores in GENERAL_RESOURCES regardless of which cache
     * handler is active — caches.match() in the SW searches all caches, so entries stored
     * here are served even when serviceWorkerCacheHandler is used.
     */
    async cachePDFPanelFilesInCacheStorage(pdfPanelModels) {
        if (!('caches' in window)) {
            return;
        }
        try {
            const cache = await caches.open('GENERAL_RESOURCES');
            await Promise.all(pdfPanelModels.map(async (model) => {
                if (!model.url) {
                    return;
                }
                const existing = await cache.match(model.url, { ignoreSearch: true });
                if (existing) {
                    const contentType = existing.headers.get('content-type') || '';
                    const contentLengthHeader = existing.headers.get('content-length');
                    const contentLength = contentLengthHeader !== null ? parseInt(contentLengthHeader, 10) : null;
                    // Treat as valid when: response is ok, content-type is PDF, and either
                    // content-length is absent (chunked encoding) or explicitly > 0.
                    // Only re-fetch when content-length is present and zero (empty/corrupt entry).
                    const isValidPdf = existing.ok
                        && contentType.includes('pdf')
                        && (contentLength === null || contentLength > 0);
                    if (isValidPdf) {
                        // The ignoreSearch lookup may have matched an entry stored under a
                        // different assessmentRunId (e.g. a previous attempt). Ensure the exact
                        // current URL is also present so caches.match(model.url) without
                        // ignoreSearch (as used in AdditionalResource.openUrl) finds it reliably.
                        const exactMatch = await cache.match(model.url);
                        if (!exactMatch) {
                            await cache.put(model.url, existing.clone());
                        }
                        return;
                    }
                }
                const response = await fetch(model.url, { credentials: 'same-origin' });
                if (response.ok) {
                    await cache.put(model.url, response);
                }
            }));
        } catch (err) {
            reportError(err);
        }
    },

    /**
     * Unconditionally caches PDFJS viewer files and PDF documents for any PDF panel
     * questions in the given assessment. Called fire-and-forget from liveDataLoader
     * after the question set loads, so files are ready before the user navigates to a
     * PDF panel question or goes offline.
     */
    async cachePDFViewerAndDocumentsForOffline(assessment) {
        if (!marketplacePropertiesService.isOfflineResilienceForPdfViewerEnabled()) {
            return;
        }
        try {
            const pdfPanelModels = mediaFinder
                .getPDFPanelModelsInQuestionSet(assessment)
                .filter(this.isCacheable);
            const { FILE } = constants.additionalResourceType;
            const fileAdditionalResourceModels = (assessment.additionalResources || [])
                .filter(r => r.type === FILE && r.logicalName)
                .map(r => ({ url: generateFileUrlForLogicalName(r.logicalName, '', assessment.assessmentRunId, FILE) }));
            if (pdfPanelModels.length === 0 && fileAdditionalResourceModels.length === 0) {
                return;
            }
            await PDFJSLoader.cacheViewerFiles();
            await this.cachePDFPanelFilesInCacheStorage([...pdfPanelModels, ...fileAdditionalResourceModels]);
        } catch (err) {
            reportError(err);
        }
    },

    async isReadinessCheckQuestionSetAlreadyCached(contentRevisionId) {
        return serviceWorkerCacheHandler
            .getExistingCacheNameForReadinessCheck(contentRevisionId)
            .then(name => !!name);
    },

    async makeRoomForANewReadinessCheckCache() {
        return serviceWorkerCacheHandler.makeRoomForANewReadinessCheckCache();
    },
};

export default cacheManager;
